import { resolveSchema, toPascalCase } from './workflowSpec.js'

export const buildSchemaRef = (category, name) => `#/schemas/${category}/${toPascalCase(name)}`

export const buildEntityRef = (schema) => {
  const hasIdField = (schema.fields || []).some((field) => field.name === 'id')

  return buildSchemaRef(hasIdField ? 'entities' : 'valueObjects', schema.name)
}

export const normalizeDescription = (input, maxLength) => {
  if (input === undefined || input === null) {
    return { value: undefined }
  }
  const trimmed = typeof input === 'string' ? input.trim() : ''
  if (trimmed.length > maxLength) {
    return { error: `Description must be at most ${maxLength} characters` }
  }

  return { value: trimmed || undefined }
}

export const resolveEntityRef = (ref, workflow) => {
  if (!ref) {
    return ref
  }
  if (!ref.startsWith('#/')) {
    return ref
  }
  const resolved = resolveSchema(ref, workflow)

  return resolved ? resolved.id : ref
}

export const resolveFieldRefs = (fields, workflow) => {
  if (!fields || !Array.isArray(fields)) {
    return fields
  }

  return fields.map((field) => {
    const { relatedEntity, fields: nestedFields, ...rest } = field
    const resolved = { ...rest }

    if (relatedEntity) {
      resolved.relatedEntityId = resolveEntityRef(relatedEntity, workflow)
    }

    if (nestedFields) {
      resolved.fields = resolveFieldRefs(nestedFields, workflow)
    }

    return resolved
  })
}

export const resolveFieldUpdateRefs = (updateFields, workflow) => {
  if (!updateFields || !Array.isArray(updateFields)) {
    return updateFields
  }

  return updateFields.map((field) => {
    if (!field.updates) {
      return field
    }
    const { relatedEntity, fields: nestedFields, ...restUpdates } = field.updates
    const resolvedUpdates = { ...restUpdates }

    if (relatedEntity !== undefined) {
      resolvedUpdates.relatedEntityId = relatedEntity ? resolveEntityRef(relatedEntity, workflow) : null
    }

    if (nestedFields) {
      resolvedUpdates.fields = resolveFieldRefs(nestedFields, workflow)
    }

    return { ...field, updates: resolvedUpdates }
  })
}

export const buildTagsFromField = (field) => {
  const tags = []
  // A key is always required.
  if (field.isRequired || field.isKey) {
    tags.push('REQUIRED')
  }
  if (field.isKey) {
    tags.push('KEY')
  }

  return tags.length > 0 ? tags : undefined
}

// Add/remove one tag, preserving the rest (a field can be both REQUIRED and KEY).
export const setTag = (tags, tag, enabled) => {
  const next = new Set(tags || [])
  if (enabled) {
    next.add(tag)
  } else {
    next.delete(tag)
  }

  return [...next]
}

export const buildNestedFields = (fields) => {
  if (!fields || !Array.isArray(fields) || fields.length === 0) {
    return undefined
  }

  const normalized = fields
    .filter((field) => field && typeof field.name === 'string' && field.name.trim().length > 0)
    .map((field) => {
      const nested = { name: field.name }
      if (field.description) {
        nested.description = field.description
      }
      if (field.relatedEntityId) {
        nested.relatedEntityId = field.relatedEntityId
      }
      if (field.cardinality) {
        nested.cardinality = field.cardinality
      }
      if (field.isRequired) {
        nested.tags = ['REQUIRED']
      }
      if (field.computed) {
        nested.computed = true
      }
      if (field.isFilter) {
        nested.type = 'filter'
      }
      const child = buildNestedFields(field.fields)
      if (child) {
        nested.fields = child
      }

      return nested
    })

  return normalized.length > 0 ? normalized : undefined
}

export const applyNestedFieldUpdates = (existingFields, updates) => {
  if (!updates || !Array.isArray(updates) || updates.length === 0) {
    return undefined
  }

  const existingByName = new Map(
    (existingFields || []).map((field) => [field.name, field])
  )

  return updates
    .filter((update) => update && typeof update.name === 'string' && update.name.trim().length > 0)
    .map((update) => {
      const existing = existingByName.get(update.name)
      const result = existing ? { ...existing } : { name: update.name }

      if (update.description !== undefined) {
        const trimmed = typeof update.description === 'string' ? update.description.trim() : ''
        if (trimmed) {
          result.description = trimmed
        } else {
          delete result.description
        }
      }
      if (update.relatedEntityId !== undefined) {
        if (update.relatedEntityId) {
          result.relatedEntityId = update.relatedEntityId
        } else {
          delete result.relatedEntityId
          delete result.fields
        }
      }
      if (update.cardinality !== undefined) {
        result.cardinality = update.cardinality
      }
      if (update.isRequired !== undefined) {
        result.tags = update.isRequired ? ['REQUIRED'] : []
      }
      if (update.computed !== undefined) {
        if (update.computed) {
          result.computed = true
        } else {
          delete result.computed
        }
      }
      if (update.isFilter !== undefined) {
        if (update.isFilter) {
          result.type = 'filter'
        } else {
          delete result.type
        }
      }
      if (update.fields !== undefined) {
        const child = applyNestedFieldUpdates(existing?.fields, update.fields)
        if (child && child.length > 0) {
          result.fields = child
        } else {
          delete result.fields
        }
      }

      return result
    })
}

export const buildSchemaField = (field, schemaType) => {
  const baseField = {
    name: field.name
  }

  switch (schemaType) {
    case 'Entity':
      return {
        ...baseField,
        description: field.description || '',
        dataType: field.dataType || 'string',
        tags: buildTagsFromField(field),
        relatedEntityId: field.relatedEntityId,
        cardinality: field.cardinality,
        exampleData: field.exampleData || ['']
      }
    case 'Command':
      return {
        ...baseField,
        relatedEntityId: field.relatedEntityId,
        hideInForm: field.hideInForm || false,
        cardinality: field.cardinality,
        tags: buildTagsFromField(field),
        fields: buildNestedFields(field.fields)
      }
    case 'Query':
      return {
        ...baseField,
        description: field.description || undefined,
        relatedEntityId: field.relatedEntityId,
        cardinality: field.cardinality,
        type: field.isFilter ? 'filter' : undefined,
        computed: field.computed || undefined,
        fields: buildNestedFields(field.fields)
      }
    case 'DomainEvent':
      return {
        ...baseField,
        relatedEntityId: field.relatedEntityId,
        cardinality: field.cardinality,
        fields: buildNestedFields(field.fields)
      }
    default:
      return baseField
  }
}
